import { reEnrolment_status } from './constant.js';

/**
 * Per-campus payment status reading.
 *
 * A family enrolled at more than one campus is charged one campus at a time, so
 * it can end up with some campuses paid and others failed / rejected / never
 * attempted. The backend reports this through several differently-shaped
 * fields (`campusStatuses`, `paidCampuses`, `campusBreakdown`, `partialPayment`,
 * `allComplete`) and the client adds its own `paymentStatus` while a payment run
 * is in progress, so all of the normalising lives here.
 */

const PAID_VALUES = ['paid', 'completed', 'complete', 'synced', 'success', 'successful', 'approved', 'settled'];

const normalise = (value) => String(value ?? '').trim().toLowerCase().replace(/[\s_-]+/g, '');

const STATUS_FIELDS = ['paymentStatus', 'payment_status', 'status', 'RenrolStatus', 'renrol_status'];

const BOOLEAN_PAID_FIELDS = ['paid', 'isPaid', 'is_paid'];

/** Fields the backend may use to state, explicitly, what is still owed. */
const OUTSTANDING_AMOUNT_FIELDS = ['outstandingAmount', 'outstanding_amount', 'amountDue', 'amount_due', 'BalanceDue'];

export const isCampusPaid = (campus) => {
    if (!campus || typeof campus !== 'object') return false;
    if (BOOLEAN_PAID_FIELDS.some((field) => campus[field] === true)) return true;
    return STATUS_FIELDS.some((field) => PAID_VALUES.includes(normalise(campus[field])));
};

const hasNothingLeftToPay = (campus) => {
    for (const field of OUTSTANDING_AMOUNT_FIELDS) {
        const amount = Number(campus[field]);
        if (campus[field] != null && Number.isFinite(amount)) return amount <= 0;
    }
    return false;
};

/**
 * Unpaid campuses only count as awaiting payment when they are not explicitly
 * settled, so a campus with no status information at all is treated as unpaid
 * rather than silently skipped.
 */
export const isCampusAwaitingPayment = (campus) => {
    if (!campus || typeof campus !== 'object') return false;
    if (isCampusPaid(campus)) return false;
    return !hasNothingLeftToPay(campus);
};

const campusKey = (campus, index) =>
    String(campus.SessionID ?? campus.session_id ?? campus.parent_code ?? campus.college ?? `row-${index}`);

const CAMPUS_LIST_FIELDS = ['campusStatuses', 'campusBreakdown', 'campuses'];

/**
 * Flattens the various campus lists a response may carry into one row per
 * campus. `paidCampuses` is a paid-only subset, so rows that arrive solely
 * through it are marked paid.
 */
export const extractCampusRows = (source) => {
    if (!source) return [];
    if (Array.isArray(source)) return source.filter(Boolean);
    if (typeof source !== 'object') return [];

    const rows = new Map();
    const merge = (list, extra = {}) => {
        (Array.isArray(list) ? list : []).forEach((row, index) => {
            if (!row || typeof row !== 'object') return;
            const key = campusKey(row, index);
            rows.set(key, { ...(rows.get(key) || {}), ...row, ...extra });
        });
    };

    CAMPUS_LIST_FIELDS.forEach((field) => merge(source[field]));
    merge(source.payment?.campusBreakdown);

    (Array.isArray(source.paidCampuses) ? source.paidCampuses : []).forEach((row, index) => {
        if (!row || typeof row !== 'object') return;
        const key = campusKey(row, index);
        const statesItsOwnStatus = BOOLEAN_PAID_FIELDS.concat(STATUS_FIELDS).some((field) => row[field] != null);
        rows.set(key, { ...(rows.get(key) || {}), ...row, ...(statesItsOwnStatus ? {} : { paid: true }) });
    });

    return Array.from(rows.values());
};

export const getCampusPaymentSummary = (source) => {
    const campuses = extractCampusRows(source);
    const paidCampuses = campuses.filter(isCampusPaid);
    const unpaidCampuses = campuses.filter(isCampusAwaitingPayment);

    return {
        campuses,
        paidCampuses,
        unpaidCampuses,
        hasPaid: paidCampuses.length > 0,
        hasUnpaid: unpaidCampuses.length > 0,
        // The case this whole module exists for: money already taken for one
        // campus while another still owes.
        isPartiallyPaid: paidCampuses.length > 0 && unpaidCampuses.length > 0,
        allPaid: campuses.length > 0 && unpaidCampuses.length === 0,
    };
};

export const isFamilyStatusComplete = (statusData) =>
    Boolean(statusData) && reEnrolment_status.includes(statusData.RenrolStatus);

/**
 * True only with positive evidence that money has been taken for one campus
 * while another still owes. A response carrying no campus information, or one
 * where nothing has been paid yet, returns false so families at the start of
 * the flow are unaffected.
 */
export const hasOutstandingCampusPayment = (statusData) => {
    if (!statusData || typeof statusData !== 'object') return false;
    if (statusData.payment?.allCampusesPaid === true) return false;
    if (statusData.partialPayment === true) return true;
    if (statusData.payment?.partialPayment === true) return true;

    const summary = getCampusPaymentSummary(statusData);
    if (summary.isPartiallyPaid) return true;
    if (statusData.allComplete === false && summary.hasPaid) return true;

    return false;
};

/**
 * Whether a family-completion redirect is safe. Family-level `RenrolStatus`
 * turns `Completed` as soon as the re-enrolment is submitted, which for a
 * multi-campus family can happen while a campus payment is still outstanding.
 */
export const shouldRedirectToCompletion = (statusData) =>
    isFamilyStatusComplete(statusData) && !hasOutstandingCampusPayment(statusData);

/** Campuses still to be charged, in the order they should be paid. */
export const getUnpaidCampuses = (source) => getCampusPaymentSummary(source).unpaidCampuses;
